import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ChatMessage, FlagRecord, PackConfidence, Reflection, ScenarioResult, TabId, TelemetryEvent, TelemetryType } from '../types/assessment';
import { generateReply } from '../utils/aiEngine';
import { evaluateScenarios } from '../utils/scoring';
const TOTAL_SECONDS = 60 * 60;
const STORAGE_KEY = 'lentic.fin-001.v0-1.session';
interface Snapshot {
  participantId: string;
  consented: boolean;
  startedAt: number | null;
  sessionId: string;
  flags: Record<string, FlagRecord>;
  openedDocs: string[];
  dwell: Record<TabId, number>;
  messages: ChatMessage[];
  events: TelemetryEvent[];
  reflection: Reflection;
  packConfidence: PackConfidence | null;
  submitted: boolean;
  submittedAt: number | null;
}
const emptySnapshot = (): Snapshot => ({
  participantId: '',
  consented: false,
  startedAt: null,
  sessionId: `S-${Math.random().toString(36).slice(2, 8).toUpperCase()}`,
  flags: {},
  openedDocs: [],
  dwell: {
    reconciliation: 0,
    budget: 0,
    documents: 0
  },
  messages: [],
  events: [],
  reflection: {
    q1: '',
    q2: '',
    q3: ''
  },
  packConfidence: null,
  submitted: false,
  submittedAt: null
});
function load(): Snapshot {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptySnapshot();
    return {
      ...emptySnapshot(),
      ...(JSON.parse(raw) as Snapshot)
    };
  } catch {
    return emptySnapshot();
  }
}
interface AssessmentValue extends Snapshot {
  remaining: number;
  elapsed: number;
  timeUp: boolean;
  locked: boolean;
  activeTab: TabId;
  thinking: boolean;
  saveState: 'idle' | 'saved';
  promptCount: number;
  results: ScenarioResult[];
  giveConsent: (participantId: string) => void;
  startAssessment: () => void;
  setActiveTab: (tab: TabId) => void;
  openDocument: (docId: string, title: string) => void;
  sendPrompt: (text: string, viaChip?: boolean) => void;
  recordPaste: (text: string) => void;
  saveFlag: (record: FlagRecord) => void;
  removeFlag: (rowId: string) => void;
  setReflection: (r: Reflection) => void;
  setPackConfidence: (c: PackConfidence) => void;
  submit: () => void;
  resetSession: () => void;
}
const AssessmentContext = createContext<AssessmentValue | null>(null);
const uid = () => Math.random().toString(36).slice(2, 10);
export function AssessmentProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [snap, setSnap] = useState<Snapshot>(() => load());
  const [activeTab, setActiveTabState] = useState<TabId>('reconciliation');
  const [thinking, setThinking] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saved'>('idle');
  const [now, setNow] = useState(() => Date.now());
  const activeTabRef = useRef<TabId>(activeTab);
  activeTabRef.current = activeTab;
  const firstRender = useRef(true);

  // Autosave — the session survives a disconnect and the clock keeps running.
  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snap));
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setSaveState('saved');
    const t = window.setTimeout(() => setSaveState('idle'), 1600);
    return () => window.clearTimeout(t);
  }, [snap]);
  const elapsed = snap.startedAt ? Math.floor((now - snap.startedAt) / 1000) : 0;
  const remaining = Math.max(0, TOTAL_SECONDS - elapsed);
  const timeUp = Boolean(snap.startedAt) && remaining <= 0;
  const locked = snap.submitted;
  useEffect(() => {
    if (!snap.startedAt || snap.submitted) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [snap.startedAt, snap.submitted]);

  // Silent dwell capture — never surfaced to the participant.
  useEffect(() => {
    if (!snap.startedAt || snap.submitted) return;
    const t = window.setInterval(() => {
      setSnap(s => ({
        ...s,
        dwell: {
          ...s.dwell,
          [activeTabRef.current]: s.dwell[activeTabRef.current] + 1
        }
      }));
    }, 1000);
    return () => window.clearInterval(t);
  }, [snap.startedAt, snap.submitted]);
  const logInto = (s: Snapshot, type: TelemetryType, detail: string): Snapshot => ({
    ...s,
    events: [...s.events, {
      id: uid(),
      at: Date.now(),
      type,
      detail
    }]
  });
  const giveConsent = useCallback((participantId: string) => {
    setSnap(s => logInto({
      ...s,
      participantId: participantId.trim(),
      consented: true
    }, 'consent', `Consent recorded for participant ${participantId.trim()}`));
  }, []);
  const startAssessment = useCallback(() => {
    setSnap(s => s.startedAt ? s : logInto({
      ...s,
      startedAt: Date.now()
    }, 'start', 'Assessment started (60:00)'));
    setNow(Date.now());
  }, []);
  const setActiveTab = useCallback((tab: TabId) => {
    setActiveTabState(prev => {
      if (prev !== tab) setSnap(s => logInto(s, 'tab', `Opened the "${tab}" tab`));
      return tab;
    });
  }, []);
  const openDocument = useCallback((docId: string, title: string) => {
    setSnap(s => logInto({
      ...s,
      openedDocs: s.openedDocs.includes(docId) ? s.openedDocs : [...s.openedDocs, docId]
    }, 'document', `Opened ${docId} — ${title}`));
  }, []);
  const recordPaste = useCallback((text: string) => {
    setSnap(s => logInto(s, 'paste', `Pasted ${text.length} characters into the assistant: "${text.replace(/\s+/g, ' ').slice(0, 90)}…"`));
  }, []);
  const sendPrompt = useCallback((text: string, viaChip = false) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const priorPrompts = snap.messages.filter(m => m.role === 'user').length;
    setSnap(s => {
      let next: Snapshot = {
        ...s,
        messages: [...s.messages, {
          id: uid(),
          role: 'user',
          content: trimmed,
          at: Date.now()
        }]
      };
      if (viaChip) next = logInto(next, 'chip', `Used suggestion chip: "${trimmed}"`);
      return logInto(next, 'prompt', trimmed.replace(/\s+/g, ' ').slice(0, 140));
    });
    setThinking(true);
    window.setTimeout(() => {
      const reply = generateReply(trimmed, priorPrompts);
      setSnap(s => ({
        ...s,
        messages: [...s.messages, {
          id: uid(),
          role: 'assistant',
          content: reply.content,
          at: Date.now()
        }]
      }));
      setThinking(false);
    }, 700 + Math.min(900, trimmed.length * 3));
  }, [snap.messages]);
  const saveFlag = useCallback((record: FlagRecord) => {
    setSnap(s => logInto({
      ...s,
      flags: {
        ...s.flags,
        [record.rowId]: record
      }
    }, record.kind === 'correction' ? 'correct' : 'flag', `${record.kind === 'correction' ? 'Correction' : 'Flag'} saved on ${record.rowId} · action ${record.actionType} · sources ${record.sources.join(', ') || 'none'} · sign-off ${record.signOff}`));
  }, []);
  const removeFlag = useCallback((rowId: string) => {
    setSnap(s => {
      const flags = {
        ...s.flags
      };
      delete flags[rowId];
      return logInto({
        ...s,
        flags
      }, 'flag', `Entry removed from ${rowId}`);
    });
  }, []);
  const setReflection = useCallback((reflection: Reflection) => {
    setSnap(s => ({
      ...s,
      reflection
    }));
  }, []);
  const setPackConfidence = useCallback((packConfidence: PackConfidence) => {
    setSnap(s => ({
      ...s,
      packConfidence
    }));
  }, []);
  const submit = useCallback(() => {
    setSnap(s => logInto({
      ...s,
      submitted: true,
      submittedAt: Date.now()
    }, 'submit', `Pack submitted · confidence "${s.packConfidence}" · ${Object.keys(s.flags).length} entries`));
  }, []);
  const resetSession = useCallback(() => {
    window.localStorage.removeItem(STORAGE_KEY);
    setSnap(emptySnapshot());
    setActiveTabState('reconciliation');
  }, []);
  const results = useMemo(() => evaluateScenarios(snap.flags, snap.openedDocs), [snap.flags, snap.openedDocs]);
  const value: AssessmentValue = {
    ...snap,
    remaining,
    elapsed,
    timeUp,
    locked,
    activeTab,
    thinking,
    saveState,
    promptCount: snap.messages.filter(m => m.role === 'user').length,
    results,
    giveConsent,
    startAssessment,
    setActiveTab,
    openDocument,
    sendPrompt,
    recordPaste,
    saveFlag,
    removeFlag,
    setReflection,
    setPackConfidence,
    submit,
    resetSession
  };
  return <AssessmentContext.Provider value={value} data-id="element-328" data-mp="10dela8b4hvs2">{children}</AssessmentContext.Provider>;
}
export function useAssessment() {
  const ctx = useContext(AssessmentContext);
  if (!ctx) throw new Error('useAssessment must be used inside AssessmentProvider');
  return ctx;
}